import React, { useRef } from 'react';
import { format, isSameDay, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useCalendar } from '../../contexts/CalendarContext';
import './Calendar.css';
// Import our custom event component
import { WeekEvent } from './EventComponent';

const WeekView = ({ 
  currentDate, 
  selectedDate, 
  events, 
  onDateSelect, 
  onEventClick
}) => {
  const navigate = useNavigate();
  const { getDaysForWeekView } = useCalendar();
  const days = getDaysForWeekView();
  const scrollContainerRef = useRef(null);
  
  // Prepare grouped events to enable consistent positioning of related events
  const prepareGroupedEvents = () => {
    if (!events || !Array.isArray(events)) return { eventGroups: {}, eventsByDay: {}, nonGroupedEvents: {} };
    
    const eventGroups = {};
    const eventsByDay = {};
    const nonGroupedEvents = {};
    
    // Initialize days object
    days.forEach((day) => {
      const dateKey = format(day.date, 'yyyy-MM-dd');
      eventsByDay[dateKey] = [];
      nonGroupedEvents[dateKey] = [];
    });
    
    // Sort and group events
    events.forEach(event => {
      // Get the date from start_date field
      const eventStartDateStr = event.start_date || (event.start && typeof event.start === 'string' ? event.start : null);
      
      let eventStartDate = null;
      
      // Handle different date formats
      if (eventStartDateStr) {
        try {
          eventStartDate = parseISO(eventStartDateStr);
        } catch (e) {
          return; // Skip this event if date can't be parsed
        }
      } else if (event.start instanceof Date) {
        eventStartDate = event.start;
      } else if (event.date instanceof Date) {
        eventStartDate = event.date;
      } else {
        return; // Skip if no valid date
      }
      
      // If valid date, add to the event map for this day
      const dateKey = format(eventStartDate, 'yyyy-MM-dd');
      if (eventsByDay[dateKey]) {
        eventsByDay[dateKey].push(event);
        
        // Group by group ID if it exists
        const groupId = event.group || null;
        
        if (groupId) {
          if (!eventGroups[groupId]) {
            eventGroups[groupId] = {
              events: [],
              groupColor: event.groupColor || '#999999' // Use the group color or default
            };
          }
          eventGroups[groupId].events.push({ event, dateKey });
        } else {
          // Save non-grouped events separately
          nonGroupedEvents[dateKey].push(event);
        }
      }
    });
    
    return { eventGroups, eventsByDay, nonGroupedEvents };
  };
  
  // Get organized data
  const { eventGroups, eventsByDay, nonGroupedEvents } = prepareGroupedEvents();
  
  // Get events for a specific day (only used for non-grouped events now)
  const getEventsForDay = (day) => {
    const dateKey = format(day.date, 'yyyy-MM-dd');
    return nonGroupedEvents[dateKey] || [];
  };

  return (
    <div className="calendar-week-view" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      
      <div className="calendar-week-grid" style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        {/* Day headers row - 7 days */}
        <div className="calendar-week-days-header">
          {days.map((day, dayIndex) => (
            <div 
              key={`header-${dayIndex}`} 
              className={`calendar-day-header ${day.isToday ? 'calendar-day-today' : ''}`}
            >
              <div>{format(day.date, 'EEE')}</div>
              <div className="calendar-day-number">{format(day.date, 'd')}</div>
            </div>
          ))}
        </div>
        
        {/* Week content with synchronized scrolling */}
        <div 
          className="calendar-week-content"
          ref={scrollContainerRef}
          style={{ 
            overflowY: 'auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            position: 'relative',
            minHeight: 0 /* This is important to allow flex child to scroll */
          }}
        >
          {/* First render grouped events - they need to be aligned in rows with 7 columns */}
          <div className="grouped-events-container" style={{ 
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            marginBottom: '10px'
          }}>
            {/* Map through group IDs */}
            {Object.keys(eventGroups).map((groupId, groupIndex) => {
              const groupData = eventGroups[groupId];
              const groupColor = groupData.groupColor || '#999999';
              
              return (
                <div 
                  key={`group-${groupId}`} 
                  className="event-group-row"
                  style={{ 
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    width: '100%',
                    marginBottom: '8px',
                    position: 'relative',
                    gap: 0
                  }}
                >
                  {/* Create placeholders for each day of the week (7 days) */}
                  {days.map((day, dayIndex) => {
                    const dateKey = format(day.date, 'yyyy-MM-dd');
                    
                    // Find if there's an event for this group on this day
                    const dayEventData = groupData.events.find(e => e.dateKey === dateKey);
                    
                    return (
                      <div 
                        key={`group-${groupId}-day-${dayIndex}`}
                        className="day-event-slot"
                        style={{
                          padding: '0 3px',
                          minHeight: '45px', /* Match the height of event components */
                          borderRight: dayIndex < 6 ? '1px solid #ddd' : 'none',
                          boxSizing: 'border-box'
                        }}
                      >
                        {dayEventData && (
                          <div 
                            className="calendar-day-event-wrapper calendar-week-event-compact"
                            style={{
                              position: 'relative',
                              marginBottom: '8px'
                            }}
                          >
                            {/* Custom positioned border that's wider than container */}
                            <div 
                              style={{
                                position: 'absolute',
                                bottom: '0px',
                                width: '90%',
                                height: '3px',
                                backgroundColor: dayEventData.event.group_color || '#999999',
                                zIndex: 2 // Ensure it appears above other elements
                              }}
                            />
                            <WeekEvent
                              event={dayEventData.event}
                              onEventClick={onEventClick}
                              compact={true}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Then render regular day columns with non-grouped events - 7 columns */}
          <div className="regular-days-container" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            flex: 1,
            width: '100%',
            gap: 0
          }}>
            {days.map((day, dayIndex) => {
              // Get non-grouped events for this day
              const dayEvents = getEventsForDay(day);
              
              return (
                <div 
                  key={`day-${dayIndex}`} 
                  className={`calendar-day-column ${day.isToday ? 'calendar-day-today' : ''} ${isSameDay(day.date, selectedDate) ? 'calendar-day-selected' : ''}`}
                  onClick={() => onDateSelect(day.date)}
                  style={{ 
                    minHeight: '100px',
                    padding: '0 3px',
                    borderRight: dayIndex < 6 ? '1px solid #ddd' : 'none'
                  }}
                >
                  <div className="calendar-day-content">
                    {/* Display events for this specific day */}
                    <div className="calendar-day-events-container">
                      {dayEvents.map((event, eventIndex) => (
                        <div 
                          key={`${event.id || eventIndex}`} 
                          className="calendar-day-event-wrapper calendar-week-event-compact"
                          style={{
                            marginBottom: '8px'
                          }}
                        >
                          <WeekEvent
                            event={event}
                            onEventClick={onEventClick}
                            compact={true}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeekView;